// FinishExam.jsx
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import API_BASE_URL from "../config/api";

const GET_RESULTS_API = `${API_BASE_URL}/get_quiz_results.php`;

const FinishExam = () => {
  /* ================= ROUTE & NAV ================= */
  const { id } = useParams();
  const startExamId = Number(id);
  const navigate = useNavigate();
  const location = useLocation();

  /* ================= USER ================= */
  let user = null;
  try {
    user = JSON.parse(sessionStorage.getItem("user"));
  } catch (e) {
    user = null;
  }

  const userId = user?.id;

  /* ================= STATE ================= */
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [results, setResults] = useState(null);
  const fetchedRef = useRef(false);

  /* ================= AUTH GUARD ================= */
  useEffect(() => {
    if (!userId) {
      navigate("/login", { replace: true });
    }
  }, [userId, navigate]);

  /* ================= LOAD RESULTS ================= */
  useEffect(() => {
    if (!userId) return;

    if (!startExamId || isNaN(startExamId)) {
      setError("Invalid Exam ID");
      setLoading(false);
      return;
    }

    // ✅ If data passed from exam page (skip API)
    if (location.state?.counts && location.state?.userAnswers) {
      setResults({
        counts: location.state.counts,
        userAnswers: location.state.userAnswers,
        start_exam_id: startExamId,
      });
      setLoading(false);
      return;
    }

    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const fetchResults = async () => {
      try {
        setLoading(true);

        const url =
          `${GET_RESULTS_API}?start_exam_id=${encodeURIComponent(startExamId)}&user_id=${encodeURIComponent(userId)}`;

        const res = await fetch(url);

        if (!res.ok) {
          throw new Error("Server error");
        }

        const data = await res.json();

        if (!data.success) {
          throw new Error(data.message || "Failed to load exam results");
        }

        setResults({
          counts: data.counts || {},
          userAnswers: data.userAnswers || [],
          start_exam_id: data.start_exam_id || startExamId,
        });

      } catch (err) {
        setError(err.message || "Unable to connect to server");
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [startExamId, userId, location.state]);

  /* ================= UI STATES ================= */
  if (loading) return <div style={{ padding: 20 }}>Loading exam results…</div>;
  if (error) return <div style={{ color: "red", padding: 20 }}>{error}</div>;
  if (!results) return <div>No results found</div>;

  /* ================= DATA ================= */
  const { counts = {}, userAnswers = [] } = results;

  const correct = counts.correct || 0;
  const notAttempted = counts.notAttempted || 0;
  const bookmark = counts.bookmark || 0;
  const total = counts.totalQuestions || userAnswers.length || 0;

  // Auto calculate attempted if not provided
  const isAttempted =
    counts.isAttempted ??
    (total - notAttempted);

  /* ================= UI ================= */
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#f9fafb",
        padding: "20px",
        fontFamily: "'Segoe UI', Tahoma, sans-serif",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "14px",
          padding: "35px 30px",
          width: "100%",
          maxWidth: "420px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          textAlign: "center",
        }}
      >
        {/* Success Icon */}
        <div
          style={{
            marginBottom: 18,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 72,
            height: 72,
            borderRadius: "50%",
            background: "#dcfce7",
            color: "#16a34a",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="40"
            height="40"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M20 6L9 17l-5-5" />
          </svg>
        </div>

        <h2 style={{ marginBottom: 8, color: "#1f2937" }}>
          Exam Completed
        </h2>

        <p style={{ color: "#4b5563", fontSize: "14px", marginBottom: 18 }}>
          You scored <strong>{correct}</strong> out of{" "}
          <strong>{total}</strong> questions.
        </p>

        {/* ===== Stats Cards ===== */}
        <div style={{ display: "flex", gap: 10, marginBottom: 22 }}>
          <div style={{ flex: 1, background: "#ecfdf5", padding: "10px", borderRadius: "8px" }}>
            <div style={{ fontSize: 12, color: "#6b7280" }}>Attempted</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#16a34a" }}>
              {isAttempted}
            </div>
          </div>

          <div style={{ flex: 1, background: "#fef2f2", padding: "10px", borderRadius: "8px" }}>
            <div style={{ fontSize: 12, color: "#6b7280" }}>Not Attempted</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#dc2626" }}>
              {notAttempted}
            </div>
          </div>

          <div style={{ flex: 1, background: "#fff7ed", padding: "10px", borderRadius: "8px" }}>
            <div style={{ fontSize: 12, color: "#6b7280" }}>Bookmarked</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#ea580c" }}>
              {bookmark}
            </div>
          </div>
        </div>

        {/* Buttons */}
        <button
          onClick={() =>
            navigate(`/view-explanation/${startExamId}`, {
              state: { userAnswers, counts, start_exam_id: startExamId },
            })
          }
          style={{
            width: "100%",
            padding: "14px",
            borderRadius: "8px",
            background: "#164d85",
            color: "#fff",
            border: "none",
            fontSize: "15px",
            fontWeight: 600,
            cursor: "pointer",
            marginBottom: 12,
          }}
        >
          View Explanation
        </button>

        {/* Retry same quiz */}
        <button
          onClick={() => navigate(`/start-exam/${startExamId}`)}
          style={{
            width: "100%",
            padding: "14px",
            borderRadius: "8px",
            background: "#e63946",
            color: "#fff",
            border: "none",
            fontSize: "15px",
            fontWeight: 600,
            cursor: "pointer",
            marginBottom: 12,
          }}
        >
          Retry Quiz
        </button>

        {/* Go to dashboard */}
        <button
          onClick={() => navigate("/studentdashboard")}
          style={{
            width: "100%",
            padding: "14px",
            borderRadius: "8px",
            background: "#09ae82",
            color: "#fff",
            border: "none",
            fontSize: "15px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Go to Dashboard
        </button>

        <div style={{ marginTop: 22, fontSize: 12, color: "#94a3b8" }}>
          powered by <strong>RSA</strong>
        </div>
      </div>
    </div>
  );
};

export default FinishExam;
